/**
 * The chair's packet summary, one letter page (People Part 7): every item asked of them this
 * year with its state and what was answered — the chair prints it from Done.
 */
import type { ReactNode } from 'react';
import { Paper, PrintRoot, formatDateTime } from '@cider/ui';
import { STATE_WORDS, type Packet } from '../views/chairs/Packet.js';
import { useLoad } from '../views/money/shared.js';

export function PacketSummary({ year }: { year: string }): ReactNode {
  /**
   * A director prints a chair's from the chair's record (A9): `?person=<id>` reads that
   * person's packet through the volunteer chairs' own door; without it, the chair's own.
   */
  const person = new URLSearchParams(window.location.search).get('person');
  const load = useLoad<Packet>(
    person
      ? `/admin/onboarding/${encodeURIComponent(person)}?year=${year}`
      : `/onboarding/packet?year=${year}`,
  );
  if (load.error) return <p role="alert">{load.error}</p>;
  const p = load.data;
  if (!p) return <p>Reading the packet…</p>;
  const required = p.items.filter((i) => i.required);
  return (
    <PrintRoot
      kind="letter"
      title={`${p.name} · ${String(p.festivalYear)} packet`}
      pages={[
        <Paper
          key="1"
          masthead={`Apple Fest ${String(p.festivalYear)} — chair packet`}
          submast={`${p.name} · ${p.says}`}
          stamp={`${String(p.done)} OF ${String(p.required)} DONE`}
          rows={required.map((i) => ({
            key: i.key,
            a: i.name,
            b:
              [
                i.signature ? `signed ${i.signature.typedName}` : null,
                i.carriedFrom ? `from ${String(i.carriedFrom)}` : null,
                i.recordedByName ? `recorded by ${i.recordedByName}` : null,
                i.doneAt ? formatDateTime(i.doneAt, { year: true }) : null,
              ]
                .filter(Boolean)
                .join(' · ') || undefined,
            c: STATE_WORDS[i.state],
            box: true,
          }))}
          foot="Keep this for your records. Anything to change: open your packet in the chair app; the volunteer chairs sees the same answers."
        />,
      ]}
    />
  );
}
